import { LATITUDE_MAX, LATITUDE_MIN, LONGITUDE_MAX, LONGITUDE_MIN } from "@utils/geocoding-utils";
import { validate as isValidUUID } from "uuid";

/**
 * Capitalizes the first letter of a string.
 *
 * @param input The string to have its first letter capitalized.
 * @returns New string with first letter in locale upper case.
 */
export function capitalizeFirstLetter(input) {
  if (!input) {
    // Return empty string if input is empty
    return "";
  }
  const firstLetter = input[0].toLocaleUpperCase();
  const remainingLetters = input.slice(1);
  return firstLetter + remainingLetters;
}
/** Capitalize the first letter of phrase depending on the capitalize method */
export function capitalizeFirstLetterInPhrase({
  phrase,
  capitalizeMethod
}) {
  const lowerCasePhrase = phrase.toLowerCase();
  return capitalizeMethod === "firstWord" ? lowerCasePhrase.charAt(0).toUpperCase() + lowerCasePhrase.slice(1) : lowerCasePhrase.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

/**
 * Removes all extra spaces from a string to have maximum one space and trims the text.
 * E.g. From " Test   Method " the three spaces are combined and trimmed into "Test Method"
 *
 * @param str The string to remove its spaces.
 * @returns New string with removed spaces.
 */
export function removeExtraSpaces(str) {
  return (str ?? "").replace(/ +(?= )/g, "").trim();
}
/**
 * Shortens the text for an specific string and adds ellipsis at the end if needed.
 */
export function shortenText({
  value,
  maxChars
}) {
  if (value === undefined || value === null) {
    return "";
  }
  if (value.length <= maxChars) {
    return value;
  }
  const ellipsis = "...";
  if (maxChars <= ellipsis.length) {
    return ellipsis.slice(0, maxChars);
  }
  const end = maxChars - ellipsis.length;
  return `${value.slice(0, end)}${ellipsis}`;
}

/**
 * Highlights a subsection of some text, by adding the <strong> tag.
 * The search it is case insensitive, but the result is kept like the original.
 * E.g.
 * Text: "Hello World" Search "wo" -> "Hello <strong>Wo</strong>rld"
 *
 * @param mainString String to look for text.
 * @param searchSubstring Search string that will be use as case insensitive.
 * @returns A react node that can be rendered to show the text
 */
export function highlightOccurrences(mainString, searchSubstring) {
  if (!mainString || !searchSubstring) {
    return [mainString];
  }
  const lowerMainString = mainString.toLowerCase();
  const lowerSearchSubstring = searchSubstring.toLowerCase();

  // Avoid iterating over every character if it does not contain the search text anyway.
  if (lowerMainString.indexOf(lowerSearchSubstring) === -1) {
    return [mainString];
  }
  const finish = [];
  for (let index = 0; index <= mainString.length; index++) {
    const foundIndex = lowerMainString.indexOf(lowerSearchSubstring, index);
    if (foundIndex === index) {
      finish.push(<strong>
          {mainString.slice(index, index + searchSubstring.length)}
        </strong>);
      index += searchSubstring.length - 1;
    } else {
      finish.push(mainString[index]);
    }
  }
  return finish;
}

/**
 * @returns True iff the passed value is a valid MongoDB ObjectId: IDs of 24 bytes(digits) hex-string:
 * https://mongodb.github.io/node-mongodb-native/api-bson-generated/objectid.html#objectid-isvalid
 * @param value string to validate
 */
export function isValidMongoDBObjectId(value) {
  const regex = /^[0-9a-fA-F]{24}$/;
  return regex.test(value);
}

/**
 * @returns True iff the passed value is a valid HoloBuilder Legacy ID: IDs of only digits of at least 9 digits length
 * @param value string to validate
 */
export function isValidHBLegacyId(value) {
  const regex = /^\d{9,}$/;
  return regex.test(value);
}

/**
 * @returns True iff the passed value is a valid project or company ID.
 * These are the valid project or company ID formats:
 * - UUID
 * - MongoDB ObjectID
 * - Custom HB Legacy ID format
 * @param value string to validate
 */
export function isValidProjectOrCompanyId(value) {
  return isValidUUID(value) || isValidMongoDBObjectId(value) || isValidHBLegacyId(value);
}

/**
 * Parses a string representing coordinates into a Position object with latitude and longitude.
 * @returns A Position object if the input string is valid, or null if the input is invalid.
 * @param input The string representing coordinates.
 */
export function parseCoordinatesString(input) {
  const sanitizedInput = input.trim();

  // Attempt to split the input in 2 parts.
  // Allowed separators are: comma, semicolon or single space
  const parts = sanitizedInput.split(/[,;\s]/);

  // Sanitize and validate parts to be defined
  const sanitizedParts = parts.map(part => part.trim());
  const definedParts = sanitizedParts.filter(part => part !== "");

  // If there are not exactly 2 defined parts, the input is invalid
  if (definedParts.length !== 2) {
    return null;
  }

  // Parse latitude and longitude values
  const lat = Number(definedParts[0]);
  const lng = Number(definedParts[1]);

  // Validate values
  if (isNaN(lat) || isNaN(lng) || lat < LATITUDE_MIN || lat > LATITUDE_MAX || lng < LONGITUDE_MIN || lng > LONGITUDE_MAX) {
    return null;
  }
  return {
    lat,
    lng
  };
}

/**
 * @returns A string representing the coordinates in the format "lat, lng".
 * @param position The Position object containing latitude and longitude.
 * If the position is undefined, an empty string is returned.
 */
export function stringifyCoordinates(position) {
  if (!position) {
    return "";
  }
  return `${position.lat}, ${position.lng}`;
}

/**
 * @returns true if the text matches at least one word from the search query.
 * Splits the search string into whitespace-separated words and checks if ANY
 * word is contained in the text (case-insensitive OR matching).
 * @param text The text to check against
 * @param search The search query string (whitespace-separated words)
 */
export function matchesAnySearchWord(text, search) {
  const lowerText = text.toLowerCase();
  const searchWords = search.toLowerCase().split(/\s+/).filter(Boolean);
  return searchWords.some(word => lowerText.includes(word));
}